import { FC, useEffect } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import useStore from '@hooks/useStore';
import useToast from '@yaydoo/react-components/lib/hooks/useToast';
import { CheckoutProps, CheckoutRouteParams } from '@containers/types';
import { PaymentLinkStatus } from '@constants/paymentLink';
import { useStyles } from './styles';
import { initOpenPay } from '@utils/openPay';
import { isProd } from '@configs/variables';

// Components
import CheckoutLayout from './components/CheckoutLayout';
import PaymentForm from './components/PaymentForm';
import PaymentSuccess from './components/PaymentSuccess';
import CashReference from '../cash-reference';
import GenericSpeiInfo from '../payment-form/SpeiPaymentForm/GenericSpeiInfo';
import OrderSummary from './components/OrderSummary';
import Loader from '@components/common/Loader';

interface CheckoutContent {
  isPaymentSuccess: boolean;
  isCashReference: boolean;
  isSpeiReference: boolean;
}

const getCheckoutContent = ({
  isPaymentSuccess,
  isCashReference,
  isSpeiReference,
}: CheckoutContent) => {
  if (isPaymentSuccess) return PaymentSuccess;
  if (isCashReference) return CashReference;
  if (isSpeiReference) return GenericSpeiInfo;
  return PaymentForm;
};

const CheckoutV2: FC<CheckoutProps> = () => {
  const { t } = useTranslation();
  const { links, checkout, dlocal, arcus, fintoc, monato } = useStore();
  const classes = useStyles();
  const { openToast } = useToast();
  const params = useParams<CheckoutRouteParams>();
  const { paymentMethods } = links.paymentConfig;

  const linkId = params.uuid;
  const isPaymentSuccess =
    checkout.success.cardPayment ||
    links.paymentLink.isPaid ||
    links.paymentLink.isSubscribed ||
    links.paymentLink.status === PaymentLinkStatus.PENDING_ACTIVATION ||
    checkout.success.directDebitPayment ||
    links.paymentLink.status === PaymentLinkStatus.SUBSCRIPTION_CANCELED;

  const isCashReference = checkout.success.cashPayment;
  const isSpeiReference =
    dlocal.flags.isSpeiReferenceGenerated ||
    arcus.flags.isSpeiReferenceGenerated ||
    fintoc.flags.isSpeiReferenceGenerated ||
    monato.flags.isSpeiReferenceGenerated;

  useEffect(() => {
    if (linkId) {
      links.getPaymentLink(linkId);
    }
  }, [linkId]);

  useEffect(() => {
    if (paymentMethods && paymentMethods.card.isPartnerOpenPay) {
      initOpenPay({
        apiKey: paymentMethods.card.publicApiKey,
        id: paymentMethods.card.merchantId,
        isProd,
      });
    }
  }, [paymentMethods?.card?.publicApiKey, paymentMethods?.card?.merchantId]);

  useEffect(() => {
    if (links.errors.fetchingLink) {
      openToast({
        type: 'error',
        title: t('checkoutScreen.errors.title'),
        message: t('checkoutScreen.errors.loadingPaymentLink'),
      });
    }
  }, [links.errors.fetchingLink]);

  const Content = getCheckoutContent({
    isPaymentSuccess,
    isCashReference,
    isSpeiReference,
  });

  if (links.flags.isRequesting || !links.paymentLink.exists) {
    return (
      <CheckoutLayout>
        <div className={classes.loadingContainer}>
          <Loader />
        </div>
      </CheckoutLayout>
    );
  }

  if (links.errors.fetchingLink) {
    return (
      <CheckoutLayout>
        <div className={classes.errorContainer}>
          <h2>{t('checkoutScreen.errors.title')}</h2>
          <p>{t('checkoutScreen.errors.loadingPaymentLink')}</p>
        </div>
      </CheckoutLayout>
    );
  }

  return (
    <CheckoutLayout>
      <div className={classes.container}>
        <div className={classes.content}>
          <Content />
        </div>
        <div className={classes.summary}>
          <OrderSummary />
        </div>
      </div>
    </CheckoutLayout>
  );
};

export default observer(CheckoutV2);
